import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { CommandBar } from './CommandBar';
import {
  SquaresFour,
  CheckCircle,
  CurrencyDollar,
  BookOpen,
  ShieldCheck,
  Key,
  TreeStructure,
  UsersThree,
  GearSix,
  MagnifyingGlass,
  SignOut,
  CreditCard,
  CaretDown,
  Gauge,
  Warning,
} from '@phosphor-icons/react';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: SquaresFour },
  { to: '/policies', label: 'Policies', icon: BookOpen },
  { to: '/approvals', label: 'Approvals', icon: CheckCircle },
  { to: '/security', label: 'Security', icon: ShieldCheck },
  { to: '/budgets', label: 'Budgets', icon: CurrencyDollar },
  { to: '/anomalies', label: 'Anomalies', icon: Warning },
  { to: '/traces', label: 'Audit Trail', icon: TreeStructure },
  { to: '/rate-limits', label: 'Rate Limits', icon: Gauge },
  { to: '/api-keys', label: 'API Keys', icon: Key },
  { to: '/team', label: 'Team', icon: UsersThree },
  { to: '/settings', label: 'Settings', icon: GearSix },
  { to: '/billing', label: 'Billing', icon: CreditCard },
];

export function Layout() {
  const { user, logout, workspaces, currentWorkspaceId, switchWorkspace } = useAuth();
  const [wsOpen, setWsOpen] = useState(false);
  const [rpm, setRpm] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentWorkspaceId) return;
    let cancelled = false;

    async function fetchRpm() {
      try {
        const data = await api.getDashboardStats(currentWorkspaceId!);
        if (!cancelled) setRpm(data.metrics.requests_per_minute);
      } catch {
        // silently ignore — header will show "—"
      }
    }

    fetchRpm();
    const interval = setInterval(fetchRpm, 30_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [currentWorkspaceId]);

  const currentWs = workspaces.find((ws) => ws.id === currentWorkspaceId);

  const handleSwitch = async (wsId: string) => {
    setWsOpen(false);
    if (wsId === currentWorkspaceId) return;
    await switchWorkspace(wsId);
    navigate('/dashboard');
  };

  return (
    <div className="flex h-screen bg-surface-0">
      {/* Sidebar */}
      <aside className="flex w-64 flex-col bg-surface-1 border-r border-border-dark">
        {/* Logo */}
        <div className="flex h-16 items-center gap-2.5 border-b border-border-dark px-6">
          <img src="/logo.png" alt="Palaryn" width="28" height="28" />
          <span className="text-lg font-semibold text-text-primary">Palaryn</span>
        </div>

        {/* Workspace switcher */}
        {workspaces.length > 0 && (
          <div className="relative border-b border-border-dark px-3 py-3">
            <button
              onClick={() => setWsOpen(!wsOpen)}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm text-text-secondary hover:bg-surface-2 transition-colors"
              aria-expanded={wsOpen}
              aria-haspopup="listbox"
            >
              <span className="truncate font-medium text-text-primary">
                {currentWs?.name || 'Select workspace'}
              </span>
              <CaretDown
                size={14}
                weight="bold"
                className={`text-text-tertiary transition-transform ${wsOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {wsOpen && (
              <ul
                role="listbox"
                className="absolute left-3 right-3 z-10 mt-1 max-h-48 overflow-y-auto rounded-lg bg-surface-2 border border-border-dark py-1 shadow-lg"
              >
                {workspaces.map((ws) => (
                  <li key={ws.id}>
                    <button
                      onClick={() => handleSwitch(ws.id)}
                      className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-surface-3 ${
                        ws.id === currentWorkspaceId
                          ? 'bg-surface-3 text-text-primary'
                          : 'text-text-secondary'
                      }`}
                      role="option"
                      aria-selected={ws.id === currentWorkspaceId}
                    >
                      <span className="truncate">{ws.name}</span>
                      {(ws as any).role && (
                        <span className="ml-2 shrink-0 rounded bg-surface-1 px-1.5 py-0.5 text-xs text-text-tertiary">
                          {(ws as any).role}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 space-y-1 px-3 py-4">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-surface-2 text-text-primary'
                    : 'text-text-secondary hover:bg-surface-2/50 hover:text-text-primary'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span
                      className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r-full"
                      style={{ background: 'linear-gradient(180deg, #06B6A4, #8B5CF6)' }}
                    />
                  )}
                  <item.icon size={20} weight="duotone" />
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User profile card */}
        <div className="border-t border-border-dark p-4">
          <div className="flex items-center gap-3">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt=""
                className="h-8 w-8 rounded-full"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-violet/20 text-accent-violet text-xs font-medium">
                {user?.display_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
            <div className="flex-1 truncate">
              <p className="truncate text-sm font-medium text-text-primary">
                {user?.display_name}
              </p>
              <p className="truncate text-xs text-text-tertiary">{user?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-16 items-center justify-between border-b border-border-dark bg-surface-1 px-8">
          {/* Search hint */}
          <button className="flex items-center gap-2 rounded-lg border border-border-dark bg-surface-2/50 px-3 py-1.5 text-sm text-text-tertiary hover:text-text-secondary hover:border-border-dark/80 transition-colors">
            <MagnifyingGlass size={16} weight="duotone" />
            <span>Search...</span>
            <kbd className="ml-4 rounded bg-surface-0 px-1.5 py-0.5 text-xs font-mono text-text-tertiary border border-border-dark">
              Cmd+K
            </kbd>
          </button>

          <div className="flex items-center gap-5">
            {/* Shield Pulse */}
            <div className="flex items-center gap-2.5 text-sm">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent-teal animate-pulse-glow" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-teal" />
              </span>
              <span className="text-text-secondary">
                <span className="text-accent-teal font-medium">{rpm !== null ? rpm.toFixed(1) : '—'}</span> req/min
              </span>
            </div>

            {/* Sign out */}
            <button
              onClick={logout}
              className="flex items-center justify-center rounded-lg p-2 text-text-tertiary hover:text-text-secondary hover:bg-surface-2 transition-colors"
              title="Sign out"
            >
              <SignOut size={18} weight="duotone" />
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-8 bg-surface-0">
          <Outlet />
        </main>
      </div>

      <CommandBar />
    </div>
  );
}
